import { useQuery } from '@tanstack/react-query';
import { Clock, ExternalLink, MapPin, TriangleAlert } from 'lucide-react';
import type { AttendancePhotoDto, AttendanceRecordDto } from '@hrms/shared';
import { api } from '@/lib/api-client';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Fetches one photo as a blob URL.
 *
 * The endpoint requires the session, so the image cannot simply be an `<img
 * src>` pointing at the API — the browser would send that request without the
 * Authorization header and get a 401. Fetching through the configured client
 * and handing the element an object URL keeps the photo behind auth.
 */
function usePunchPhoto(recordId: string, kind: string, enabled: boolean) {
  return useQuery({
    queryKey: ['punch-photo', recordId, kind],
    enabled,
    // Never cached beyond the dialog: this is a photograph of a person, and it
    // should leave memory when they close it.
    gcTime: 0,
    staleTime: 0,
    queryFn: async () => {
      const response = await api.get<Blob>(`/attendance/photos/${recordId}/${kind}`, {
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    },
  });
}

/**
 * The map square the punch was taken in, with the pin where it actually fell.
 *
 * The tile comes from our own API rather than straight from OpenStreetMap: the
 * page's CSP allows images from `self` only, so a direct tile URL renders as a
 * broken image, and a direct request would also hand OpenStreetMap the viewer's
 * address alongside an employee's whereabouts.
 *
 * The pin's position inside the tile arrives as headers on the same response,
 * so there is no second round trip and no projection maths in the browser.
 */
function PinnedMap({ recordId, kind }: { recordId: string; kind: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['punch-map', recordId, kind],
    gcTime: 0,
    staleTime: 0,
    retry: false,
    queryFn: async () => {
      const response = await api.get<Blob>(`/attendance/photos/${recordId}/${kind}/map`, {
        responseType: 'blob',
      });

      return {
        url: URL.createObjectURL(response.data),
        // Percentages, so the pin stays put when the 256-pixel tile is scaled
        // to whatever width the dialog gives it.
        left: (Number(response.headers['x-pin-x'] ?? 128) / 256) * 100,
        top: (Number(response.headers['x-pin-y'] ?? 128) / 256) * 100,
      };
    },
  });

  if (isLoading) return <Skeleton className="aspect-square w-full rounded-lg" />;

  // No map is not an error worth shouting about — the coordinates are still
  // listed above it, and a punch with no fix is the ordinary indoor case.
  if (error || !data) return null;

  return (
    <div className="space-y-1">
      <div className="relative overflow-hidden rounded-lg border">
        <img src={data.url} alt="" className="w-full" />
        <span
          aria-hidden="true"
          style={{ left: `${data.left}%`, top: `${data.top}%` }}
          className="absolute -ml-2 -mt-2 size-4 rounded-full border-2 border-background bg-destructive shadow"
        />
      </div>
      {/* Required by the map data's licence, not decoration. */}
      <p className="text-[11px] text-muted-foreground">Map data © OpenStreetMap contributors</p>
    </div>
  );
}

function PhotoPane({ recordId, photo }: { recordId: string; photo: AttendancePhotoDto }) {
  const { data: url, isLoading, error } = usePunchPhoto(recordId, photo.kind, true);

  const captured = new Date(photo.capturedAt);
  const hasFix = photo.latitude !== null && photo.longitude !== null;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">{photo.kind === 'TIME_IN' ? 'Clock in' : 'Clock out'}</p>

      {isLoading && <Skeleton className="aspect-[3/4] w-full rounded-lg" />}

      {error && (
        <Alert variant="destructive">
          <TriangleAlert aria-hidden="true" />
          <AlertDescription>
            The image could not be loaded. If it is past the retention window it has been
            deleted, and this record is all that remains of it.
          </AlertDescription>
        </Alert>
      )}

      {url && (
        <img
          src={url}
          alt={`${photo.kind === 'TIME_IN' ? 'Clock in' : 'Clock out'} photo`}
          className="w-full rounded-lg border"
        />
      )}

      {/*
        The stored columns, shown beside the picture on purpose. The stamp burnt
        into the image is written by the phone before upload and can be edited
        like any other pixels; these came from the server. If the two disagree,
        the image is what is wrong.
      */}
      <dl className="space-y-1.5 rounded-lg border bg-muted/40 p-3 text-sm">
        <div className="flex gap-2">
          <Clock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Recorded by the server
            </dt>
            <dd className="tabular-nums">
              {captured.toLocaleString('en-PH', { timeZone: 'Asia/Manila' })}
            </dd>
          </div>
        </div>

        <div className="flex gap-2">
          <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Location reported by the device
            </dt>
            <dd className="tabular-nums">
              {hasFix ? (
                <>
                  {photo.latitude}, {photo.longitude}
                  {photo.accuracyMeters !== null && (
                    <span className="text-muted-foreground"> ±{photo.accuracyMeters} m</span>
                  )}
                  <a
                    className="ml-2 inline-flex items-center gap-1 text-xs underline underline-offset-2"
                    href={`https://www.openstreetmap.org/?mlat=${photo.latitude}&mlon=${photo.longitude}#map=17/${photo.latitude}/${photo.longitude}`}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Open
                    <ExternalLink className="size-3" aria-hidden="true" />
                  </a>
                </>
              ) : (
                <span className="text-muted-foreground">No fix recorded</span>
              )}
            </dd>
          </div>
        </div>
      </dl>

      {/* Only when there is something to pin. */}
      {hasFix && <PinnedMap recordId={recordId} kind={photo.kind} />}

      {photo.isClockSuspect && photo.clockSkewSeconds !== null && (
        <Alert variant="warning">
          <TriangleAlert aria-hidden="true" />
          <AlertTitle>This device&apos;s clock was wrong</AlertTitle>
          <AlertDescription>
            The phone reported a time {Math.abs(Math.round(photo.clockSkewSeconds / 60))} minute(s){' '}
            {photo.clockSkewSeconds > 0 ? 'ahead of' : 'behind'} the server. The punch was
            recorded at the server&apos;s time, so pay is unaffected — but the time printed on
            the photo is the wrong one, and a clock set deliberately looks exactly like this.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

interface PunchPhotoDialogProps {
  record: AttendanceRecordDto | null;
  onOpenChange: (open: boolean) => void;
}

export function PunchPhotoDialog({ record, onOpenChange }: PunchPhotoDialogProps) {
  const photos = record?.photos ?? [];

  return (
    <Dialog open={record !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {record?.employeeName} — {record?.date}
          </DialogTitle>
          <DialogDescription>
            Nothing here confirms who is in the frame, and a device can report a false
            position. Treat it as a record of what was submitted, not proof of attendance.
          </DialogDescription>
        </DialogHeader>

        {photos.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No photos on this record. It was entered by hand, punched before the capture was
            switched on, or the photos have passed their retention window.
          </p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2">
            {photos
              .slice()
              .sort((a, b) => (a.kind === 'TIME_IN' ? -1 : 1) - (b.kind === 'TIME_IN' ? -1 : 1))
              .map((photo) => (
                <PhotoPane key={photo.kind} recordId={record!.id} photo={photo} />
              ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
